import React from 'react';
import { cn } from '@/lib/cn';
import { SpriteIcon } from '@/components/ui/IconSprite';

export interface SectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The main title text to display */
  title?: string;
  /** The subtitle text or content to display. If provided, subtitle will be shown. Can be a string or ReactNode (for links, etc.) */
  subtitleText?: string | React.ReactNode;
  /**
   * Trailing adornment. Pass "default" to show default chevron, or provide a custom icon element.
   */
  trailingIcon?: 'default' | React.ReactElement | undefined | boolean;
  /**
   * Whether the entire header container is interactive (clickable).
   * Defaults to true when trailingIcon is 'default' or true, false when trailingIcon is a custom React element.
   */
  isHeaderInteractive?: boolean;
  /** Whether to use inverted colors */
  isInverted?: boolean;
  /** The HTML element to use for the title (h1, h2, h3, etc.) */
  element?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  titleClassName?: string;
  titleContainerClassName?: string;
  subtitleContainerClassName?: string;
}

export function SectionHeader({
  title = 'Recommended listings',
  subtitleText,
  trailingIcon,
  isHeaderInteractive,
  className,
  isInverted = false,
  element = 'h2',
  titleClassName = 'md:text-heading-5 text-heading-6',
  titleContainerClassName = '',
  subtitleContainerClassName = '',
  ...props
}: SectionHeaderProps) {
  const HeadingTag = element || 'h2';

  const trailingEl = React.useMemo(() => {
    if (trailingIcon === true || trailingIcon === 'default') {
      return (
        <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
          <SpriteIcon name="chevron-right" className="w-full h-full icon-primary" />
        </div>
      );
    }

    if (React.isValidElement(trailingIcon)) {
      return trailingIcon;
    }

    return null;
  }, [trailingIcon]);

  // Determine if header should be interactive:
  // - If isHeaderInteractive is explicitly provided, use that
  // - Otherwise, default to true when trailingIcon is 'default' or true (entire header is clickable)
  // - Default to false when trailingIcon is a custom React element (only the element is clickable)
  const isInteractive =
    isHeaderInteractive !== undefined
      ? isHeaderInteractive
      : trailingIcon === true || trailingIcon === 'default';
  // Note: subtitle rendering handled by checking subtitleText below

  return (
    <div
      className={cn('section-header inline-flex flex-col items-start justify-center', className)}
      {...props}
    >
      {/* Title Container - matches Figma design */}
      <div
        className={cn(
          'flex gap-1 items-center px-2 md:px-4 py-1 rounded-full',
          // Interactive states - only when trailingIcon is true
          isInteractive && 'cursor-pointer transition-colors duration-200 hover:bg-secondary',
          titleContainerClassName,
        )}
        // onClick removed to avoid navigation side-effects
      >
        {/* Title - H5 typography (text-xl, 20px) */}
        {React.createElement(
          element,
          {
            className: cn(isInverted ? 'text-inverted' : 'text-primary', titleClassName),
          },
          title,
        )}

        {/* Trailing Arrow Icon - 12px */}
        {trailingEl}
      </div>

      {/* Subtitle Container - only show if subtitleText is provided - matches Figma design */}
      {subtitleText && (
        <div
          className={cn(
            'flex items-center justify-center px-2 md:px-4 py-0 w-full',
            subtitleContainerClassName,
          )}
        >
          {/* Subtitle - Caption typography */}
          {typeof subtitleText === 'string' ? (
            <p
              className={cn('flex-1 text-caption', isInverted ? 'text-inverted' : 'text-secondary')}
            >
              {subtitleText}
            </p>
          ) : (
            <div
              className={cn('flex-1 text-caption', isInverted ? 'text-inverted' : 'text-secondary')}
            >
              {subtitleText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
